<template>
  <div>
    <ListToolbar
      v-model:search="search"
      search-placeholder="Search API keys..."
      :total="pagination.total"
      :page="pagination.page"
      :limit="pagination.limit"
      v-model:sort="sort"
      :sort-options="sortOptions"
      @reload="fetchKeys"
      @page-change="goToPage"
    >
      <template #actions>
        <button v-if="$checkPermission('api_keys.create')" @click="openCreate" class="btn-primary">
          <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          New API Key
        </button>
      </template>
    </ListToolbar>

    <!-- Table -->
    <div v-if="loading || items.length" :class="{ 'animate-fade-in': !loading }" class="bg-white rounded-xl border border-gray-200/60 table-scroll card-shadow">
      <table class="w-full">
        <thead>
          <tr class="bg-gray-50 border-b border-gray-100">
            <th class="text-left text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5">Name</th>
            <th class="text-left text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5">API Key</th>
            <th class="text-left text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5 hidden md:table-cell">Status</th>
            <th class="text-left text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5 hidden lg:table-cell">Created</th>
            <th class="text-left text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5 hidden lg:table-cell">Last Used</th>
            <th class="text-right text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5">Actions</th>
          </tr>
        </thead>
        <tbody v-if="loading">
          <tr v-for="i in 4" :key="'skel-' + i" class="border-b border-gray-50">
            <td class="px-6 py-4"><div class="h-3.5 bg-gray-100 rounded skeleton-pulse" :style="{ width: (40 + i * 8) + '%' }" /></td>
            <td class="px-6 py-4"><div class="h-3.5 bg-gray-100 rounded skeleton-pulse w-40" /></td>
            <td class="px-6 py-4 hidden md:table-cell"><div class="h-6 bg-gray-100 rounded-full skeleton-pulse w-16" /></td>
            <td class="px-6 py-4 hidden lg:table-cell"><div class="h-3.5 bg-gray-100 rounded skeleton-pulse w-24" /></td>
            <td class="px-6 py-4 hidden lg:table-cell"><div class="h-3.5 bg-gray-100 rounded skeleton-pulse w-20" /></td>
            <td class="px-6 py-4"><div class="h-8 bg-gray-100 rounded-lg skeleton-pulse w-8 ml-auto" /></td>
          </tr>
        </tbody>
        <tbody v-else class="divide-y divide-gray-50">
          <tr v-for="item in items" :key="item._id" class="hover:bg-gray-50/50 transition-colors">
            <td class="px-6 py-4">
              <p class="text-[14px] text-gray-900">{{ item.name }}</p>
            </td>
            <td class="px-6 py-4">
              <div class="flex items-center gap-2">
                <code class="text-sm font-data text-gray-500 bg-gray-50 px-2 py-0.5 rounded">{{ item.api_key }}</code>
                <button @click="copyText(item.api_key)" class="p-1 rounded text-gray-400 hover:text-[var(--color-primary)] transition-colors" title="Copy">
                  <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                </button>
              </div>
            </td>
            <td class="px-6 py-4 hidden md:table-cell">
              <span :class="item.status === 'active' ? 'badge-live' : 'badge-idle'">{{ item.status }}</span>
            </td>
            <td class="px-6 py-4 hidden lg:table-cell">
              <span class="text-sm text-gray-500">{{ $filters.formatDatetime(item.created_at) }}</span>
            </td>
            <td class="px-6 py-4 hidden lg:table-cell">
              <span class="text-sm text-gray-500">{{ item.last_used_at ? $filters.formatDatetime(item.last_used_at) : '—' }}</span>
            </td>
            <td class="px-6 py-4 text-right">
              <div class="flex items-center justify-end gap-1">
                <ActionsMenu :items="keyActions(item)" />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Empty: no matches -->
    <div v-else-if="!loading && search" class="bg-white rounded-xl border border-gray-200/60 px-6 py-20 text-center card-shadow">
      <div class="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
        <svg class="w-6 h-6 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
      </div>
      <p class="text-base font-medium text-gray-900">No matching API keys found</p>
      <p class="text-sm text-gray-500 mt-1">Try adjusting your search</p>
    </div>

    <!-- Empty: no records -->
    <div v-else-if="!loading" class="bg-white rounded-xl border border-gray-200/60 px-6 py-20 text-center card-shadow">
      <div class="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
        <svg class="w-6 h-6 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 11-7.778 7.778 5.5 5.5 0 017.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/></svg>
      </div>
      <p class="text-base font-medium text-gray-900">No API keys yet</p>
      <p class="text-sm text-gray-500 mt-1">Create your first API key to integrate with external services</p>
    </div>

    <!-- Create/Edit Modal -->
    <Teleport to="body">
      <Transition name="modal">
        <div v-if="showModal" class="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div class="absolute inset-0 bg-black/30 backdrop-blur-sm" />
          <div class="relative bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col animate-fade-in">
            <div class="flex items-center justify-between px-6 pt-6 shrink-0">
              <h3 class="text-lg font-semibold text-gray-900">{{ editItem ? 'Edit API Key' : 'New API Key' }}</h3>
              <button @click="showModal = false" class="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100">
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <form @submit.prevent="save" class="flex-1 flex flex-col overflow-hidden">
              <div class="flex-1 overflow-y-auto p-6 pt-5 space-y-5">
                <div>
                  <label class="block text-sm font-medium text-gray-600 mb-1.5">Name</label>
                  <input v-model="form.name" type="text" placeholder="e.g. Mobile App, Website" />
                </div>

                <!-- Scopes -->
                <div>
                  <label class="block text-sm font-medium text-gray-600 mb-1.5">Scopes</label>
                  <div class="grid grid-cols-2 gap-2">
                    <label v-for="scope in availableScopes" :key="scope.value" :class="['flex items-center gap-2.5 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors', form.scopes.includes(scope.value) ? 'border-[var(--color-primary)] bg-[var(--color-primary-light)]' : 'border-gray-200 hover:border-gray-300']">
                      <input type="checkbox" :value="scope.value" v-model="form.scopes" class="w-4 h-4 rounded border-gray-300 text-[var(--color-primary)] focus:ring-[var(--color-primary)]" />
                      <div>
                        <p class="text-sm font-medium" :class="form.scopes.includes(scope.value) ? 'text-[var(--color-primary)]' : 'text-gray-700'">{{ scope.label }}</p>
                      </div>
                    </label>
                  </div>
                  <p class="text-xs text-gray-400 mt-1.5">Leave empty for full access</p>
                </div>

                <!-- Rate Limit -->
                <div>
                  <label class="block text-sm font-medium text-gray-600 mb-1.5">Rate Limit <span class="text-xs text-gray-400 font-normal">(requests per minute)</span></label>
                  <input v-model.number="form.rate_limit" type="number" min="0" placeholder="0 = unlimited" />
                </div>

                <!-- Whitelist / Blacklist -->
                <div class="grid grid-cols-2 gap-4">
                  <div>
                    <label class="block text-sm font-medium text-gray-600 mb-1.5">
                      IP Whitelist
                      <span class="text-xs text-gray-400 font-normal">(one per line)</span>
                    </label>
                    <textarea v-model="form.whitelistText" rows="3" placeholder="192.168.1.0/24&#10;10.0.0.1" class="font-data text-xs" />
                    <p class="text-xs text-gray-400 mt-1">Only these IPs can use this key</p>
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-600 mb-1.5">
                      IP Blacklist
                      <span class="text-xs text-gray-400 font-normal">(one per line)</span>
                    </label>
                    <textarea v-model="form.blacklistText" rows="3" placeholder="203.0.113.0/24&#10;198.51.100.1" class="font-data text-xs" />
                    <p class="text-xs text-gray-400 mt-1">These IPs will be blocked</p>
                  </div>
                </div>
              </div>
              <div class="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 shrink-0">
                <button type="button" @click="showModal = false" class="btn-secondary">Cancel</button>
                <button type="submit" :disabled="saving" class="btn-primary">{{ saving ? 'Saving...' : 'Save' }}</button>
              </div>
            </form>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Secret Display Modal (shown once after creation or regeneration) -->
    <Teleport to="body">
      <Transition name="modal">
        <div v-if="showSecretModal" class="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div class="absolute inset-0 bg-black/30 backdrop-blur-sm" />
          <div class="relative bg-white rounded-2xl shadow-xl w-full max-w-md animate-fade-in">
            <div class="px-6 pt-6">
              <h3 class="text-lg font-semibold text-gray-900">API Secret</h3>
              <p class="text-sm text-gray-400 mt-1">Copy your secret now — it won't be shown again.</p>
            </div>
            <div class="p-6 space-y-4">
              <div class="flex items-start gap-2.5 px-3.5 py-2.5 rounded-lg bg-amber-50 border border-amber-100">
                <svg class="w-4 h-4 text-amber-500 shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                <p class="text-sm text-amber-700">This secret will only be displayed once. Store it securely.</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-600 mb-1.5">Secret Key</label>
                <div class="flex items-center gap-2">
                  <code class="flex-1 text-sm font-data text-gray-900 bg-gray-50 px-3 py-2 rounded-lg border border-gray-200 break-all">{{ revealedSecret }}</code>
                  <button @click="copyText(revealedSecret)" class="btn-secondary !px-3 !py-2 shrink-0">Copy</button>
                </div>
              </div>
            </div>
            <div class="px-6 py-4 border-t border-gray-100 flex justify-end">
              <button @click="showSecretModal = false" class="btn-primary">Done</button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <ConfirmModal
      :show="showDeleteModal" :loading="deleting"
      title="Delete API Key"
      :message="`Delete '${deleteItem?.name}'? This key will immediately stop working.`"
      confirm-text="Delete" :danger="true"
      @confirm="deleteKey" @cancel="showDeleteModal = false"
    />

    <ConfirmModal
      :show="showRegenerateModal" :loading="regenerating"
      title="Regenerate Secret"
      :message="`Regenerate the secret for '${regenerateItem?.name}'? The old secret will stop working immediately.`"
      confirm-text="Regenerate" :danger="true"
      @confirm="doRegenerate" @cancel="showRegenerateModal = false"
    />

    <ConfirmModal
      :show="showToggleModal"
      :loading="toggling"
      :title="toggleItem?.status === 'active' ? 'Deactivate API Key' : 'Activate API Key'"
      :message="toggleItem?.status === 'active' ? `Deactivate '${toggleItem?.name}'? Requests using this key will be rejected.` : `Activate '${toggleItem?.name}'? Requests using this key will be accepted.`"
      :confirm-text="toggleItem?.status === 'active' ? 'Deactivate' : 'Activate'"
      :danger="toggleItem?.status === 'active'"
      @confirm="doToggleStatus"
      @cancel="showToggleModal = false"
    />
  </div>
</template>

<script>
import ListToolbar from '../../components/ListToolbar.vue'
import ActionsMenu from '../../components/ActionsMenu.vue'
import ConfirmModal from '../../components/ConfirmModal.vue'
import icons from '../../components/action-icons.js'
import api from '../../services/api.js'
import { notifySuccess, notifyError } from '../../services/notifications.js'

export default {
  name: 'ApiKeysList',
  components: { ListToolbar, ActionsMenu, ConfirmModal },

  data() {
    return {
      loading: true,
      items: [],
      search: '',
      sort: 'created:-1',
      sortOptions: [
        { value: 'name:1', label: 'Name A-Z' },
        { value: 'name:-1', label: 'Name Z-A' },
        { value: 'created:1', label: 'Created (oldest)' },
        { value: 'created:-1', label: 'Created (newest)' }
      ],
      pagination: { limit: 10, page: 1, total: 0 },
      // Modal
      showModal: false,
      saving: false,
      editItem: null,
      form: { name: '', scopes: [], rate_limit: null, whitelistText: '', blacklistText: '' },
      availableScopes: [
        { value: 'livestreams.read', label: 'Lives Read' },
        { value: 'medias.read', label: 'Medias Read' }
      ],
      // Secret
      showSecretModal: false,
      revealedSecret: '',
      // Delete
      showDeleteModal: false,
      deleting: false,
      deleteItem: null,
      // Regenerate
      showRegenerateModal: false,
      regenerating: false,
      regenerateItem: null,
      // Toggle status
      showToggleModal: false,
      toggling: false,
      toggleItem: null
    }
  },

  watch: {
    search() { this.pagination.page = 1; this.fetchKeys() },
    sort() { this.pagination.page = 1; this.fetchKeys() }
  },

  mounted() { this.fetchKeys() },

  methods: {
    async fetchKeys(count = true) {
      try {
        const params = { limit: this.pagination.limit, page: this.pagination.page }
        if (this.search) params.text = this.search
        if (this.sort) params.sort = this.sort
        if (count) params.count = true
        const res = await api.get('/api-keys', { params })
        this.items = res.data?.items || []
        if (Number.isInteger(res.data?.total)) this.pagination.total = res.data.total
      } catch { /* keep */ } finally { this.loading = false }
    },

    goToPage(page) { this.pagination.page = page; this.fetchKeys(false) },

    openCreate() {
      this.editItem = null
      this.form = { name: '', scopes: [], rate_limit: null, whitelistText: '', blacklistText: '' }
      this.showModal = true
    },

    openEdit(item) {
      this.editItem = item
      this.form = {
        name: item.name,
        scopes: item.scopes || [],
        rate_limit: item.rate_limit || null,
        whitelistText: (item.whitelist || []).join('\n'),
        blacklistText: (item.blacklist || []).join('\n')
      }
      this.showModal = true
    },

    buildPayload() {
      const whitelist = this.form.whitelistText.split('\n').map(s => s.trim()).filter(Boolean)
      const blacklist = this.form.blacklistText.split('\n').map(s => s.trim()).filter(Boolean)
      return {
        name: this.form.name,
        scopes: this.form.scopes,
        rate_limit: this.form.rate_limit || null,
        whitelist,
        blacklist
      }
    },

    async save() {
      if (!this.form.name?.trim()) { notifyError('Name is required'); return }
      this.saving = true
      try {
        const payload = this.buildPayload()
        if (this.editItem) {
          await api.put(`/api-keys/${this.editItem._id}`, payload)
          notifySuccess('API key updated')
        } else {
          const res = await api.post('/api-keys', payload)
          if (res.data?.secret) {
            this.revealedSecret = res.data.secret
            this.showSecretModal = true
          }
          notifySuccess('API key created')
        }
        this.showModal = false
        this.fetchKeys()
      } catch (err) {
        notifyError(err?.message || 'Failed to save')
      } finally { this.saving = false }
    },

    keyActions(item) {
      const canUpdate = this.$checkPermission('api_keys.update')
      const active = item.status === 'active'
      return [
        { label: 'Edit', show: canUpdate, action: () => this.openEdit(item), icon: icons.edit },
        { label: 'Copy API Key', action: () => this.copyText(item.api_key), icon: icons.copy },
        { divider: true },
        { label: active ? 'Deactivate' : 'Activate', show: canUpdate, action: () => this.toggleStatus(item), icon: active ? icons.pause : icons.play },
        { label: 'Regenerate Secret', show: canUpdate, action: () => this.confirmRegenerate(item), icon: icons.refresh },
        { divider: true },
        { label: 'Delete', show: this.$checkPermission('api_keys.delete'), danger: true, action: () => this.confirmDelete(item), icon: icons.delete }
      ]
    },

    toggleStatus(item) {
      this.toggleItem = item
      this.showToggleModal = true
    },
    async doToggleStatus() {
      this.toggling = true
      try {
        const newStatus = this.toggleItem.status === 'active' ? 'inactive' : 'active'
        await api.put(`/api-keys/${this.toggleItem._id}`, { status: newStatus })
        notifySuccess(`API key ${newStatus === 'active' ? 'activated' : 'deactivated'}`)
        this.fetchKeys()
      } catch (err) {
        notifyError(err?.message || 'Failed to update')
      } finally {
        this.toggling = false
        this.showToggleModal = false
        this.toggleItem = null
      }
    },

    confirmRegenerate(item) {
      this.regenerateItem = item
      this.showRegenerateModal = true
    },

    async doRegenerate() {
      if (!this.regenerateItem) return
      this.regenerating = true
      try {
        const res = await api.put(`/api-keys/${this.regenerateItem._id}/regenerate`)
        if (res.data?.secret) {
          this.revealedSecret = res.data.secret
          this.showSecretModal = true
        }
        notifySuccess('Secret regenerated')
        this.showRegenerateModal = false
        this.regenerateItem = null
      } catch (err) {
        notifyError(err?.message || 'Failed to regenerate')
      } finally { this.regenerating = false }
    },

    confirmDelete(item) {
      this.deleteItem = item
      this.showDeleteModal = true
    },

    async deleteKey() {
      if (!this.deleteItem) return
      this.deleting = true
      try {
        await api.delete(`/api-keys/${this.deleteItem._id}`)
        notifySuccess('API key deleted')
        this.showDeleteModal = false
        this.deleteItem = null
        this.fetchKeys()
      } catch (err) {
        notifyError(err?.message || 'Failed to delete')
      } finally { this.deleting = false }
    },

    async copyText(text) {
      try {
        await navigator.clipboard.writeText(text)
        notifySuccess('Copied to clipboard')
      } catch { notifyError('Failed to copy') }
    }
  }
}
</script>

<style scoped>
.modal-enter-active, .modal-leave-active { transition: opacity 0.2s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; }
</style>
